import type { MatchData, PlayerStat } from '../types'
import { teamSummaries } from '../data/match'
import { SectionLabel } from './RoundTimeline'

export function Scoreboard({ match, ourTeam }: { match: MatchData; ourTeam: string }) {
  const [winner, loser] = teamSummaries(match)
  const byTeam = (team: string) =>
    match.scoreboard.filter((p) => p.team === team).sort((a, b) => b.rating - a.rating)
  return (
    <section
      className="border border-border bg-surface p-5"
      style={{ borderRadius: 'var(--radius)' }}
    >
      <SectionLabel n="01" label="Scoreboard" />
      <div className="mt-4 space-y-5">
        {[winner, loser].map((t) => (
          <TeamTable
            key={t.name}
            team={t.name}
            score={t.score}
            isOurs={t.name === ourTeam}
            players={byTeam(t.name)}
          />
        ))}
      </div>
    </section>
  )
}

const COLS = ['K', 'D', 'A', '+/−', 'ADR', 'KAST', 'HS%', 'OK', 'OD', 'TK', 'MK', 'RTG']

function TeamTable({
  team,
  score,
  isOurs,
  players,
}: {
  team: string
  score: number
  isOurs: boolean
  players: PlayerStat[]
}) {
  return (
    <div>
      <div className="mb-2 flex items-baseline gap-2">
        <span className={`font-display text-base font-semibold ${isOurs ? 'text-accent' : 'text-text'}`}>
          {team}
        </span>
        <span className="font-display text-sm text-text-dim tabular-nums">{score}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-text-faint">
              <th className="py-1.5 pr-2 text-left font-medium">Player</th>
              {COLS.map((c) => (
                <th key={c} className="px-2 py-1.5 text-right font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <PlayerRow key={p.steamid64} p={p} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PlayerRow({ p }: { p: PlayerStat }) {
  return (
    <tr className="border-t border-border/60 hover:bg-surface-2">
      <td className="py-2 pr-2 text-left font-medium text-text">{p.name}</td>
      <Num v={p.kills} />
      <Num v={p.deaths} dim />
      <Num v={p.assists} dim />
      <td className="px-2 py-2 text-right tabular-nums" style={{ color: plusMinusColor(p.plus_minus) }}>
        {p.plus_minus > 0 ? `+${p.plus_minus}` : p.plus_minus}
      </td>
      <Num v={p.adr.toFixed(0)} />
      <Num v={`${p.kast.toFixed(0)}%`} dim />
      <Num v={`${p.hs_pct.toFixed(0)}%`} dim />
      <Num v={p.opening_kills} />
      <Num v={p.opening_deaths} dim />
      <Num v={p.trade_kills} dim />
      <td className="px-2 py-2 text-right text-[11px] text-text-dim tabular-nums">{fmtMulti(p.multi_kills)}</td>
      <td
        className="px-2 py-2 text-right font-display font-semibold tabular-nums"
        style={{ color: ratingColor(p.rating) }}
      >
        {p.rating.toFixed(2)}
      </td>
    </tr>
  )
}

function Num({ v, dim }: { v: number | string; dim?: boolean }) {
  return (
    <td className={`px-2 py-2 text-right tabular-nums ${dim ? 'text-text-dim' : 'text-text'}`}>{v}</td>
  )
}

function fmtMulti(mk: Record<string, number>): string {
  const parts = Object.entries(mk)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n}×${k}K`)
  return parts.length ? parts.join(' ') : '–'
}

function plusMinusColor(v: number): string {
  if (v > 0) return 'var(--accent)'
  if (v < 0) return 'var(--loss)'
  return 'var(--text-dim)'
}

function ratingColor(r: number): string {
  if (r >= 1.15) return 'var(--accent-bright)'
  if (r >= 1.0) return 'var(--accent)'
  if (r >= 0.85) return 'var(--text)'
  return 'var(--loss)'
}
